import React, { useEffect, useState, useRef } from "react";
import { Table, Spin, message, Input, Button } from "antd";
import { API_BASE_URL } from "../api/api";
import { Link } from "react-router-dom";
import update from "immutability-helper";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import * as XLSX from "xlsx";
import { ExportOutlined } from "@ant-design/icons";

const type = "DragableHeaderColumn";

const DragableHeaderCell = ({
  index,
  moveColumn,
  className,
  style,
  ...restProps
}) => {
  const ref = useRef();

  const [{ isOver }, drop] = useDrop({
    accept: type,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
    hover(item) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      moveColumn(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  let draggingStyle = {};
  if (isDragging) {
    draggingStyle = {
      background: "#e6f7ff",
      border: "2px dashed #1890ff",
      opacity: 0.7,
    };
  } else if (isOver) {
    draggingStyle = {
      background: "#f0f5ff",
    };
  }

  return (
    <th
      ref={ref}
      className={className}
      style={{ cursor: "move", userSelect: "none", ...style, ...draggingStyle }}
      {...restProps}
    />
  );
};

const VacationOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [displayColumns, setDisplayColumns] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
  const userRole = userInfo.role_id;

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}vacationPackageOrder`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json();
      const ordersList = json.data || [];

      const maxPassengers = Math.max(
        ...ordersList.map((order) => order.passengers?.length || 0)
      );

      const passengerCols = [];
      for (let i = 0; i < maxPassengers; i++) {
        passengerCols.push(
          {
            title: `Passenger ${i + 1} Name`,
            dataIndex: `passenger_${i + 1}_name`,
            key: `passenger_${i + 1}_name`,
            render: (_, record) => {
              const p = record.passengers?.[i];
              return p ? `${p.first_name} ${p.last_name}` : "-";
            },
          },
          {
            title: `Passenger ${i + 1} Age`,
            dataIndex: `passenger_${i + 1}_age`,
            key: `passenger_${i + 1}_age`,
            render: (_, record) => {
              const p = record.passengers?.[i];
              return p ? p.age : "-";
            },
          }
        );
      }

      const baseCols = initColumns.filter(
        (col) => col.dataIndex !== "passengers"
      );
      const allCols = [...baseCols, ...passengerCols];
      let filteredCols =
        userRole === 3
          ? allCols.filter(
              (col) =>
                col.dataIndex !== "commission" &&
                col.dataIndex !== "agent_code" &&
                col.dataIndex !== "member_id"
            )
          : allCols;
      setDisplayColumns(filteredCols);

      setOrders(ordersList);
      setFilteredOrders(ordersList);
    } catch (error) {
      console.error(error);
      message.error("Failed to load vacation orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    const lower = searchTerm.toLowerCase();
    const filtered = orders.filter((order) => {
      return (
        String(order.order_id).toLowerCase().includes(lower) ||
        (order.primary_passenger_first_name || "")
          .toLowerCase()
          .includes(lower) ||
        (order.hotel_name || "").toLowerCase().includes(lower)
      );
    });
    setFilteredOrders(filtered);
  }, [searchTerm, orders]);

  const initColumns = [
    { title: "ID", dataIndex: "id", sorter: (a, b) => a.id - b.id },
    {
      title: "Created Time",
      dataIndex: "add_time",
      sorter: (a, b) => (a.add_time || "").localeCompare(b.add_time || ""),
    },
    {
      title: "Order Number",
      dataIndex: "order_id",
      key: "order_id",
      sorter: (a, b) => a.order_id - b.order_id,
      render: (order_id) => (
        <Link to={`/vacation-orders/${order_id}`}>{order_id}</Link>
      ),
    },
    {
      title: "Agenct Code",
      dataIndex: "agent_code",
      key: "agent_code",
      sorter: (a, b) => a.agent_code - b.agent_code,
    },
    {
      title: "Status",
      dataIndex: "status", // assuming the backend provides it as `status`
      key: "status",
      render: (status) => {
        const isConfirmed = String(status) === "1";
        return (
          <span
            style={{
              color: isConfirmed ? "green" : "red",
              fontWeight: "bold",
            }}
          >
            {isConfirmed ? "Confirmed" : "Cancelled"}
          </span>
        );
      },
      filters: [
        { text: "Confirmed", value: "1" },
        { text: "Cancelled", value: "0" },
      ],
      onFilter: (value, record) => String(record.status) === value,
    },
    {
      title: "Member ID",
      dataIndex: "member_id",
      key: "member_id",
      render: (member_id) => <Link to={`/user/${member_id}`}>{member_id}</Link>,
      sorter: (a, b) => a.member_id - b.member_id,
    },
    {
      title: "Tour Operator",
      dataIndex: "tour_operator",
      sorter: (a, b) =>
        (a.tour_operator || "").localeCompare(b.tour_operator || ""),
    },
    {
      title: "Hotel Name",
      dataIndex: "hotel_name",
      sorter: (a, b) => (a.hotel_name || "").localeCompare(b.hotel_name || ""),
    },
    {
      title: "Departure City",
      dataIndex: "gateway_name",
      sorter: (a, b) =>
        (a.gateway_name || "").localeCompare(b.gateway_name || ""),
    },
    {
      title: "Departure Date",
      dataIndex: "departure_date",
      sorter: (a, b) => new Date(a.departure_date) - new Date(b.departure_date),
    },
    {
      title: "Destination City",
      dataIndex: "destination_name",
      sorter: (a, b) =>
        (a.destination_name || "").localeCompare(b.destination_name || ""),
    },
    {
      title: "Return Date",
      dataIndex: "return_date",
      sorter: (a, b) => new Date(a.return_date) - new Date(b.return_date),
      render: (return_date) =>
        return_date.slice(0, 4) +
        "-" +
        return_date.slice(4, 6) +
        "-" +
        return_date.slice(6, 8),
    },
    {
      title: "# of Passengers",
      dataIndex: "passenger_count",
      sorter: (a, b) => a.passenger_count - b.passenger_count,
    },
    {
      title: "Passenger Name",
      dataIndex: "primary_passenger_first_name",
      sorter: (a, b) =>
        (a.primary_passenger_first_name || "").localeCompare(
          b.primary_passenger_first_name || ""
        ),
    },
    {
      title: "Total Trip Cost",
      dataIndex: "total_trip_cost",
      sorter: (a, b) =>
        parseFloat(a.total_trip_cost || 0) - parseFloat(b.total_trip_cost || 0),
      render: (cost) => `$${parseFloat(cost).toFixed(2)}`,
    },
    {
      title: "Commission",
      dataIndex: "commission",
      sorter: (a, b) => parseFloat(a.commission) - parseFloat(b.commission),
      render: (val) => `$${parseFloat(val).toFixed(2)}`,
    },
    {
      title: userRole === 3 ? "Commission" : "Pay-Out",
      dataIndex: "payout",
      sorter: (a, b) => parseFloat(a.payout || 0) - parseFloat(b.payout || 0),
      render: (val) => {
        if (val === null || val === undefined || val === "") return "";
        return `$${parseFloat(val).toFixed(2)}`;
      },
    },
    {
      title: "Hotel Star",
      dataIndex: "hotel_star",
      sorter: (a, b) => parseInt(a.hotel_star) - parseInt(b.hotel_star),
    },
  ];

  const moveColumn = (dragIndex, hoverIndex) => {
    const dragCol = displayColumns[dragIndex];
    const newCols = update(displayColumns, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, dragCol],
      ],
    });
    setDisplayColumns(newCols);
  };

  const components = {
    header: {
      cell: (props) => (
        <DragableHeaderCell {...props} moveColumn={moveColumn} />
      ),
    },
  };

  const finalColumns = displayColumns.map((col, index) => ({
    ...col,
    onHeaderCell: () => ({ index, moveColumn }),
  }));

  const handleExport = () => {
    const exportData = filteredOrders.map((row) => {
      const output = {};
      displayColumns.forEach((col) => {
        let cellValue;
        if (col.render) {
          const rendered = col.render(row[col.dataIndex], row);
          if (typeof rendered === "string" || typeof rendered === "number") {
            cellValue = rendered;
          } else if (React.isValidElement(rendered)) {
            cellValue = rendered.props.children ?? ""; // Attempt to extract string from <Link> or other nodes
          } else {
            cellValue = "";
          }
        } else {
          cellValue = row[col.dataIndex] ?? "";
        }
        output[col.title] = cellValue;
      });
      return output;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Vacation Orders");
    XLSX.writeFile(workbook, "vacation_orders.xlsx");
  };

  // Compute summary values
  const totalBookings = filteredOrders.length;
  const totalCommission = filteredOrders.reduce(
    (sum, order) => sum + parseFloat(order.commission || 0),
    0
  );
  const totalPayout = filteredOrders.reduce(
    (sum, order) => sum + parseFloat(order.payout || 0),
    0
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <div>
        <h2>Vacation Package Orders</h2>
        <div
          style={{
            display: "flex",
            gap: "16px",
            marginBottom: "16px",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              flex: "1 1 200px",
              padding: "16px",
              border: "1px solid #d9d9d9",
              borderRadius: "8px",
              backgroundColor: "#fafafa",
              fontWeight: 500,
              fontSize: "16px",
            }}
          >
            <div>Number of Bookings</div>
            <div
              style={{ fontSize: "24px", color: "#1890ff", fontWeight: 700 }}
            >
              {totalBookings}
            </div>
          </div>

          {userRole === 3 ? (
            <div
              style={{
                flex: "1 1 200px",
                padding: "16px",
                border: "1px solid #d9d9d9",
                borderRadius: "8px",
                backgroundColor: "#fafafa",
                fontWeight: 500,
                fontSize: "16px",
              }}
            >
              <div>Total Commission</div>
              <div
                style={{ fontSize: "24px", color: "#52c41a", fontWeight: 700 }}
              >
                ${totalPayout.toFixed(2)}
              </div>
            </div>
          ) : (
            <>
              <div
                style={{
                  flex: "1 1 200px",
                  padding: "16px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "8px",
                  backgroundColor: "#fafafa",
                  fontWeight: 500,
                  fontSize: "16px",
                }}
              >
                <div>Total Commission</div>
                <div
                  style={{
                    fontSize: "24px",
                    color: "#52c41a",
                    fontWeight: 700,
                  }}
                >
                  ${totalCommission.toFixed(2)}
                </div>
              </div>
              <div
                style={{
                  flex: "1 1 200px",
                  padding: "16px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "8px",
                  backgroundColor: "#fafafa",
                  fontWeight: 500,
                  fontSize: "16px",
                }}
              >
                <div>Total Payout</div>
                <div
                  style={{
                    fontSize: "24px",
                    color: "#fa541c",
                    fontWeight: 700,
                  }}
                >
                  ${totalPayout.toFixed(2)}
                </div>
              </div>
            </>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Input
            placeholder="Search by Order Number, Passenger Name, or Hotel Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginBottom: 16, maxWidth: 460 }}
            allowClear
          />
          <Button
            type="primary"
            onClick={handleExport}
            icon={<ExportOutlined />}
            style={{
              fontSize: "16px",
              fontWeight: "bold",
              borderRadius: "8px",
              backgroundColor: "rgba(0, 128, 0, 0.1)",
              color: "#008000",
              border: "1px solid #008000",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            }}
            onMouseOver={(e) =>
              (e.target.style.backgroundColor = "rgba(0, 128, 0, 0.2)")
            }
            onMouseOut={(e) =>
              (e.target.style.backgroundColor = "rgba(0, 128, 0, 0.1)")
            }
          >
            Export to Excel
          </Button>
        </div>
        {loading ? (
          <Spin size="large" />
        ) : (
          <>
            <div
              style={{
                margin: "16px 0",
                padding: "8px 16px",
                backgroundColor: "#fafafa",
                border: "1px solid #d9d9d9",
                borderRadius: "8px",
                fontWeight: 500,
                fontSize: "14px",
                color: "#555",
              }}
            >
              Showing{" "}
              <span style={{ color: "#1890ff", fontWeight: 600 }}>
                {filteredOrders.length}
              </span>{" "}
              result{filteredOrders.length !== 1 ? "s" : ""} found
            </div>
            <div className="compact-table-wrapper">
              <Table
                columns={finalColumns}
                components={components}
                dataSource={filteredOrders}
                rowKey="id"
                bordered
                scroll={{ x: "max-content" }}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "25", "50", "100"],
                }}
              />
            </div>
          </>
        )}
      </div>
    </DndProvider>
  );
};

export default VacationOrdersPage;
